import { IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Edit, Visibility as VisibilityIcon, Delete } from '@mui/icons-material';
import { Fragment, ReactElement, useCallback, useState } from 'react';
import {
  ActiveTableActions,
  ItemCtaVisibility,
} from 'system/components/shared/list-item-cta-menu/types';
import { useNavigate } from 'react-router';
import { MunicipalityClient } from 'system/geographic/pages/municipality/list/types';

interface ExtendedItemCtaVisibility extends ItemCtaVisibility {
  updateName?: boolean;
  ended?: boolean;
}

interface CtaMenuMunicipalitiesProps {
  cell: MunicipalityClient;
  onTrashPress: (cell: MunicipalityClient) => void;
  hidden?: ExtendedItemCtaVisibility;
  active?: ActiveTableActions;
}

const CtaMenuMunicipalities = ({
  cell,
  onTrashPress,
  hidden = {
    edit: false,
    read: false,
    delete: false,
    ended: false,
    updateName: false,
  },
  active = {
    read: true,
    edit: true,
    delete: true,
  },
}: CtaMenuMunicipalitiesProps): ReactElement => {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleEvent = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onVisibilityPress = useCallback((): void => {
    const ended = cell.isEnded ? 'soppresso' : '';
    const foreign = cell.isForeign ? 'estero' : 'italia';
    const redirect = ended || foreign;
    navigate(`/sistema/geografiche/comune/${redirect}/dettaglio/${cell.code}`);
  }, [navigate, cell]);

  const onEditPress = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const onUpdatePress = useCallback((): void => {
    const ended = cell.isEnded ? 'soppresso' : '';
    const foreign = cell.isForeign ? 'estero' : 'italia';
    const redirect = ended || foreign;
    navigate(`/sistema/geografiche/comune/${redirect}/modifica/${cell.code}`);
  }, [navigate, cell]);

  const onNameChangePress = useCallback((): void => {
    const ended = cell.isEnded ? 'soppresso' : '';
    const foreign = cell.isForeign ? 'estero' : 'italia';
    const redirect = ended || foreign;
    navigate(`/sistema/geografiche/comune/${redirect}/nome/${cell.code}`);
  }, [navigate, cell]);

  const onEndedPress = useCallback((): void => {
    navigate(`/sistema/geografiche/comune/cessazione/${cell.code}`);
  }, [navigate, cell]);

  const handleClose = useCallback(() => {
    return new Promise((resolve) => resolve(setAnchorEl(null)));
  }, [setAnchorEl]);

  return (
    <Fragment>
      {!hidden.read ? (
        <Tooltip
          arrow
          title={
            active.read
              ? 'Visualizza'
              : "Non si ha l'autorizzazione per accedere a questa funzionalità"
          }
        >
          <span>
            <IconButton
              onClick={(event) => {
                handleEvent(event);
                onVisibilityPress();
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              disabled={!active.read}
              style={{
                padding: '4px',
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
      {!hidden.edit ? (
        <Tooltip
          arrow
          title={active.edit ? '' : "Non si ha l'autorizzazione per accedere a questa funzionalità"}
        >
          <span>
            <IconButton
              aria-label="Modifica elemento"
              aria-haspopup="true"
              onClick={(event) => {
                handleEvent(event);
                onEditPress(event);
              }}
              disabled={!active.edit}
              style={{
                padding: '4px',
              }}
              id="edit-button"
            >
              <Edit />
            </IconButton>
            <Menu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'edit-button',
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  handleClose().then(() => {
                    onUpdatePress();
                  });
                }}
              >
                <Typography variant="inherit">Aggiornamento</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose().then(() => {
                    onNameChangePress();
                  });
                }}
              >
                <Typography variant="inherit" noWrap>
                  Cambio Denominazione
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose().then(() => {
                    onEndedPress();
                  });
                }}
                disabled={!hidden.ended}
              >
                <Typography variant="inherit">Cessazione</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose().then(() => {
                    onTrashPress(cell);
                  });
                }}
                disabled={!hidden.ended}
              >
                <Typography variant="inherit">Cancellazione Fisica</Typography>
              </MenuItem>
            </Menu>
          </span>
        </Tooltip>
      ) : null}
      {!hidden.delete ? (
        <Tooltip
          arrow
          title={
            active.delete
              ? 'Annulla'
              : "Non si ha l'autorizzazione per accedere a questa funzionalità"
          }
        >
          <span>
            <IconButton
              aria-label="Annulla elemento"
              aria-haspopup="true"
              color="cdmBrand"
              onClick={(event) => {
                handleEvent(event);
                onTrashPress(cell);
              }}
              disabled={!active.delete}
              style={{
                padding: '4px',
              }}
            >
              <Delete />
            </IconButton>
          </span>
        </Tooltip>
      ) : null}
    </Fragment>
  );
};

CtaMenuMunicipalities.displayName = 'List Cta Dot Menu municipalities';

export default CtaMenuMunicipalities;
